// DashboardRecrutement.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Bar, Pie, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEraser, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

// ---------- Dexie cache ----------
const db = new Dexie('RecrutementDashboardCache');
db.version(1).stores({ cache: 'key, timestamp' });
const CACHE_KEY = 'recrutementStats';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24h

// ---------- API ----------
const API_URL = `${API_CONFIG2.apiUrl}candidat/dashboard_recrutement.php`;

// ---------- Dédoublonnage des requêtes ----------
const pendingFetches = new Map();

// ---------- Hook personnalisé optimisé ----------
const useRecrutementData = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [source, setSource] = useState('cache');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);

  const clearError = useCallback(() => setError(null), []);

  const loadFromCache = useCallback(async () => {
    try {
      const cached = await db.cache.get(CACHE_KEY);
      if (cached && cached.timestamp && (Date.now() - cached.timestamp) < CACHE_TTL) {
        return cached.data;
      }
      return null;
    } catch { return null; }
  }, []);

  const saveToCache = useCallback(async (data) => {
    try { await db.cache.put({ key: CACHE_KEY, data, timestamp: Date.now() }); } catch {}
  }, []);

  const loadStaleCache = useCallback(async () => {
    try {
      const cached = await db.cache.get(CACHE_KEY);
      return cached ? cached.data : null;
    } catch { return null; }
  }, []);

  const fetchData = useCallback(async (forceRefresh = false, retryCount = 0) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const pendingKey = forceRefresh ? `${CACHE_KEY}_force` : CACHE_KEY;

    if (pendingFetches.has(pendingKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(pendingKey);
        if (isMounted.current && result) {
          setData(result);
          setSource('cache (partagé)');
          setLastUpdate(new Date());
          setLoading(false);
          setIsRefreshing(false);
        }
        return result;
      } catch {
        pendingFetches.delete(pendingKey);
      }
    }

    if (forceRefresh) {
      if (isMounted.current) setIsRefreshing(true);
    } else {
      if (isMounted.current) setLoading(true);
    }

    const fetchPromise = (async () => {
      try {
        if (!forceRefresh) {
          const cached = await loadFromCache();
          if (cached) {
            if (isMounted.current) {
              setData(cached);
              setSource('cache');
              setLastUpdate(new Date());
              setLoading(false);
            }
            return cached;
          }
        }

        const url = forceRefresh ? `${API_URL}?_t=${Date.now()}` : API_URL;
        const response = await fetch(url, { signal: controller.signal });

        if (response.status === 429) {
          const stale = await loadStaleCache();
          if (stale) {
            if (isMounted.current) {
              setData(stale);
              setSource('cache (périmé)');
              setLastUpdate(new Date());
              setLoading(false);
              setIsRefreshing(false);
            }
            return stale;
          }
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchData(forceRefresh, retryCount + 1);
        }

        if (!response.ok) {
          const stale = await loadStaleCache();
          if (stale) {
            if (isMounted.current) {
              setData(stale);
              setSource('cache (périmé)');
              setLastUpdate(new Date());
              setLoading(false);
              setIsRefreshing(false);
            }
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const json = await response.json();
        if (json.error) throw new Error(json.error);

        if (isMounted.current) {
          setData(json);
          setSource(forceRefresh ? 'live (forcé)' : 'live');
          setLastUpdate(new Date());
          setLoading(false);
          setIsRefreshing(false);
          await saveToCache(json);
        }
        return json;
      } catch (err) {
        if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
          if (isMounted.current) {
            setLoading(false);
            setIsRefreshing(false);
          }
          return null;
        }

        const stale = await loadStaleCache();
        if (stale && isMounted.current) {
          setData(stale);
          setSource('cache (périmé)');
          setLastUpdate(new Date());
          setLoading(false);
          setIsRefreshing(false);
          return stale;
        }

        if (isMounted.current) {
          setError(err.message || 'Erreur de chargement des données');
          setLoading(false);
          setIsRefreshing(false);
        }
        throw err;
      }
    })();

    pendingFetches.set(pendingKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(pendingKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [loadFromCache, saveToCache, loadStaleCache]);

  useEffect(() => {
    isMounted.current = true;
    fetchData(false);
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [fetchData]);

  const refresh = useCallback(async () => {
    if (isRefreshing || loading) return;
    setIsRefreshing(true);
    setError(null);
    await fetchData(true);
  }, [fetchData, isRefreshing, loading]);

  const clearCache = useCallback(async () => {
    try {
      await db.cache.delete(CACHE_KEY);
      await fetchData(true);
    } catch (err) {
      if (isMounted.current) {
        setError('Erreur lors du vidage du cache : ' + err.message);
      }
    }
  }, [fetchData]);

  return { data, loading, error, source, lastUpdate, isRefreshing, refresh, clearCache, clearError };
};

// ---------- Composant principal ----------
const DashboardRecrutement = () => {
  const { data, loading, error, source, isRefreshing, refresh, clearCache, clearError } = useRecrutementData();

  // États pour le toast
  const [toast, setToast] = useState(null);
  const toastTimeoutRef = useRef(null);

  const showToast = useCallback((message, variant = 'success', duration = 4000) => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ message, variant });
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
      toastTimeoutRef.current = null;
    }, duration);
  }, []);

  const handleClearCache = useCallback(async () => {
    try {
      await clearCache();
      showToast('Cache vidé avec succès', 'success');
    } catch (err) {
      showToast('Erreur lors du vidage du cache', 'danger');
    }
  }, [clearCache, showToast]);

  // Défaut des données en cas d'absence
  const defaultData = {
    totaux: { offres: 0, candidatures: 0, candidats: 0 },
    postes: { ouverts: 0, pourvus: 0 },
    moyenneCandidaturesParOffre: 0,
    statutsOffres: [],
    statutsCandidatures: [],
    services: [],
    sexes: [],
    evolution: [],
    offresPopulaires: [],
    niveauxEtude: []
  };

  const finalData = data || defaultData;

  // Mémorisation des données de graphiques (basée sur finalData)
  const chartData = useMemo(() => {
    const statutsOffres = finalData.statutsOffres || [];
    const statutsCandidatures = finalData.statutsCandidatures || [];
    const services = finalData.services || [];
    const sexes = finalData.sexes || [];
    const evolution = finalData.evolution || [];
    const offresPop = finalData.offresPopulaires || [];
    const niveaux = finalData.niveauxEtude || [];
    const totalCandidats = finalData.totaux?.candidats || 1;

    return {
      barStatutsOffres: {
        labels: statutsOffres.map(s => s.statut),
        datasets: [{
          label: 'Offres par statut',
          data: statutsOffres.map(s => s.nb),
          backgroundColor: 'rgba(74, 138, 255, 0.7)',
          borderColor: '#4a8aff',
          borderWidth: 2,
          borderRadius: 6,
        }],
      },
      pieCandidatures: {
        labels: statutsCandidatures.map(s => s.statut),
        datasets: [{
          data: statutsCandidatures.map(s => s.nb),
          backgroundColor: ['#4a8aff', '#2ecc71', '#f39c12', '#ff6b9d', '#8b5cf6'],
          borderColor: 'rgba(11,20,40,0.6)',
          borderWidth: 2,
        }],
      },
      barServices: {
        labels: services.map(s => s.service),
        datasets: [{
          label: 'Offres par service',
          data: services.map(s => s.nb),
          backgroundColor: 'rgba(255, 159, 64, 0.7)',
          borderColor: '#f39c12',
          borderWidth: 2,
          borderRadius: 6,
        }],
      },
      doughnutSexes: {
        labels: sexes.map(s => s.sexe),
        datasets: [{
          data: sexes.map(s => s.nb),
          backgroundColor: ['#4a8aff', '#ff6b9d', '#2ecc71'],
          borderColor: 'rgba(11,20,40,0.6)',
          borderWidth: 2,
        }],
      },
      lineEvolution: {
        labels: evolution.map(e => e.mois),
        datasets: [{
          label: 'Candidatures par mois',
          data: evolution.map(e => e.nb),
          borderColor: '#00d4ff',
          backgroundColor: 'rgba(0,212,255,0.1)',
          tension: 0.4,
          fill: true,
          pointBackgroundColor: '#4a8aff',
        }],
      },
      barPopulaires: {
        labels: offresPop.map(o => o.titre),
        datasets: [{
          label: 'Candidatures reçues',
          data: offresPop.map(o => o.nb),
          backgroundColor: 'rgba(46, 204, 113, 0.7)',
          borderColor: '#2ecc71',
          borderWidth: 2,
          borderRadius: 6,
        }],
      },
      progressNiveaux: niveaux.map(n => ({
        label: n.niveau_etude,
        count: n.nb,
        percentage: (n.nb / totalCandidats) * 100,
      })),
    };
  }, [finalData]);

  // États de chargement/erreur (si pas de données ET en cours de chargement ou erreur)
  if (loading && !data) {
    return (
      <div className="loader-wrapper">
        <div className="loader-spinner"></div>
        <p className="loader-text">Chargement des données de recrutement...</p>
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="error-wrapper">
        <p>{error}</p>
        <button onClick={refresh} className="retry-btn">Réessayer</button>
      </div>
    );
  }

  // À ce stade, on a soit des données réelles, soit l'objet par défaut.
  // Le tableau s'affiche toujours.

  const {
    barStatutsOffres,
    pieCandidatures,
    barServices,
    doughnutSexes,
    lineEvolution,
    barPopulaires,
    progressNiveaux,
  } = chartData;

  const totaux = finalData.totaux || { offres: 0, candidatures: 0, candidats: 0 };
  const postes = finalData.postes || { ouverts: 0, pourvus: 0 };
  const moyenneCandParOffre = finalData.moyenneCandidaturesParOffre || 0;

  const getChartOptions = (type, isHorizontal = false) => ({
    responsive: true,
    maintainAspectRatio: true,
    indexAxis: isHorizontal ? 'y' : 'x',
    plugins: {
      legend: {
        position: window.innerWidth < 600 ? 'bottom' : 'top',
        labels: {
          color: '#9bb0d9',
          font: { size: window.innerWidth < 600 ? 10 : 12 },
          boxWidth: window.innerWidth < 600 ? 12 : 20,
          padding: window.innerWidth < 600 ? 8 : 12,
        },
      },
    },
    scales: type === 'bar' || type === 'line' ? {
      x: {
        ticks: { color: '#9bb0d9', font: { size: window.innerWidth < 600 ? 9 : 11 } },
        grid: { color: 'rgba(255,255,255,0.05)' },
      },
      y: {
        ticks: { color: '#9bb0d9', font: { size: window.innerWidth < 600 ? 9 : 11 } },
        grid: { color: 'rgba(255,255,255,0.05)' },
        beginAtZero: true,
      },
    } : {},
  });

  // Composant helper pour l'affichage des graphiques vides
  const renderChart = (chartComponent, dataList, message = 'Aucune donnée disponible') => {
    if (!dataList || dataList.length === 0) {
      return <div className="text-center text-secondary py-3">{message}</div>;
    }
    return chartComponent;
  };

  return (
    <div className="dashboard-wrapper">
      <style>{`
        /* ---------- RESET & BASE ---------- */
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { 
          background: #0b1428; 
          color: #f0f4ff; 
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          line-height: 1.5;
        }
        .dashboard-wrapper {
          max-width: 1640px;
          margin: 0 auto;
          padding: clamp(0.8rem, 2vw, 2rem);
          min-height: 100vh;
          background: radial-gradient(ellipse at 10% 20%, rgba(74,138,255,0.06) 0%, transparent 60%),
                      radial-gradient(ellipse at 90% 80%, rgba(124,92,252,0.04) 0%, transparent 50%);
        }

        /* ---------- ANIMATIONS ---------- */
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 8px rgba(46,204,113,0.3); }
          50% { box-shadow: 0 0 20px rgba(46,204,113,0.6); }
        }
        @keyframes scaleIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        /* ---------- GLASS CARD ---------- */
        .glass {
          background: rgba(12, 28, 58, 0.55);
          backdrop-filter: blur(16px) saturate(1.2);
          -webkit-backdrop-filter: blur(16px) saturate(1.2);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.4);
          transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
        }
        .glass:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 48px rgba(0,0,0,0.5);
          border-color: rgba(74,138,255,0.25);
        }

        /* ---------- HEADER ---------- */
        .dashboard-header {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: center;
          padding: clamp(0.6rem, 1.2vw, 1.2rem) clamp(0.8rem, 1.5vw, 1.8rem);
          margin-bottom: clamp(1rem, 2vw, 1.8rem);
          background: rgba(12, 28, 58, 0.5);
          backdrop-filter: blur(16px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.3);
          animation: fadeUp 0.5s ease;
        }
        .dashboard-header h1 {
          font-size: clamp(1.2rem, 2.5vw, 1.8rem);
          font-weight: 700;
          background: linear-gradient(135deg, #4a8aff, #00d4ff);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .dashboard-header h1 small {
          font-size: clamp(0.65rem, 1.2vw, 0.9rem);
          font-weight: 400;
          -webkit-text-fill-color: #9bb0d9;
        }
        .header-right {
          display: flex;
          align-items: center;
          gap: clamp(6px, 1vw, 14px);
          flex-wrap: wrap;
        }
        .header-time {
          font-size: clamp(0.55rem, 0.9vw, 0.75rem);
          color: #9bb0d9;
          background: rgba(255,255,255,0.05);
          padding: 4px 12px;
          border-radius: 40px;
          border: 1px solid rgba(255,255,255,0.06);
          display: flex;
          align-items: center;
          gap: 6px;
          white-space: nowrap;
        }
        .header-time .dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #2ecc71;
          animation: pulseGlow 2s infinite;
        }
        .badge-source {
          font-size: clamp(0.5rem, 0.8vw, 0.7rem);
          padding: 3px 12px;
          border-radius: 40px;
          background: rgba(74,138,255,0.15);
          color: #4a8aff;
          border: 1px solid rgba(74,138,255,0.12);
          white-space: nowrap;
          font-weight: 500;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .btn-refresh {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.08);
          color: #9bb0d9;
          padding: 5px 16px;
          border-radius: 40px;
          cursor: pointer;
          font-size: clamp(0.55rem, 0.9vw, 0.75rem);
          transition: all 0.25s;
          display: flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          touch-action: manipulation;
        }
        .btn-refresh:hover:not(:disabled) {
          background: rgba(74,138,255,0.15);
          color: #fff;
          border-color: #4a8aff;
          transform: scale(1.02);
        }
        .btn-refresh:active:not(:disabled) {
          transform: scale(0.95);
        }
        .btn-refresh:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }
        .btn-clear-cache {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(239,68,68,0.2);
          color: #f87171;
          padding: 5px 16px;
          border-radius: 40px;
          cursor: pointer;
          font-size: clamp(0.55rem, 0.9vw, 0.75rem);
          transition: all 0.25s;
          display: flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          touch-action: manipulation;
        }
        .btn-clear-cache:hover:not(:disabled) {
          background: rgba(239,68,68,0.1);
          border-color: #ef4444;
          color: #fff;
          transform: scale(1.02);
        }
        .btn-clear-cache:active:not(:disabled) {
          transform: scale(0.95);
        }
        .btn-clear-cache:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }
        .spinner-small {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255,255,255,0.1);
          border-top-color: #4a8aff;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
        }

        /* ---------- KPI GRID ---------- */
        .kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(clamp(130px, 18vw, 190px), 1fr));
          gap: clamp(10px, 1.5vw, 18px);
          margin-bottom: clamp(1.2rem, 2vw, 2rem);
        }
        .kpi-card {
          padding: clamp(0.7rem, 1.2vw, 1.2rem);
          background: rgba(12, 28, 58, 0.5);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 14px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.3);
          transition: all 0.25s ease;
          animation: fadeUp 0.4s ease;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .kpi-card:hover {
          transform: translateY(-4px);
          border-color: rgba(74,138,255,0.3);
          box-shadow: 0 8px 30px rgba(0,0,0,0.4);
        }
        .kpi-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .kpi-icon {
          font-size: clamp(1.3rem, 2vw, 1.8rem);
          opacity: 0.9;
        }
        .kpi-value {
          font-size: clamp(1.5rem, 3vw, 2.2rem);
          font-weight: 700;
          background: linear-gradient(135deg, #4a8aff, #00d4ff);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          line-height: 1.2;
        }
        .kpi-value.small {
          font-size: clamp(0.9rem, 1.8vw, 1.2rem);
          background: none;
          -webkit-text-fill-color: #f0f4ff;
        }
        .kpi-label {
          font-size: clamp(0.55rem, 0.9vw, 0.7rem);
          color: #9bb0d9;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 600;
        }
        .kpi-sub {
          font-size: clamp(0.5rem, 0.7vw, 0.6rem);
          color: #6a80aa;
          margin-top: 2px;
        }

        /* ---------- CHART GRID ---------- */
        .chart-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: clamp(14px, 1.8vw, 24px);
          margin-bottom: clamp(1.2rem, 2vw, 2rem);
        }
        .chart-container {
          background: rgba(12, 28, 58, 0.5);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
          padding: clamp(0.8rem, 1.5vw, 1.6rem);
          box-shadow: 0 4px 20px rgba(0,0,0,0.3);
          transition: all 0.25s ease;
          animation: fadeUp 0.5s ease;
          min-height: 220px;
          display: flex;
          flex-direction: column;
        }
        .chart-container:hover {
          border-color: rgba(74,138,255,0.2);
          box-shadow: 0 8px 30px rgba(0,0,0,0.4);
        }
        .chart-title {
          font-size: clamp(0.85rem, 1.4vw, 1.05rem);
          font-weight: 600;
          margin-bottom: 10px;
          display: flex;
          align-items: center;
          gap: 8px;
          color: #f0f4ff;
          flex-wrap: wrap;
        }
        .chart-title .badge {
          font-size: clamp(0.5rem, 0.7vw, 0.6rem);
          font-weight: 500;
          background: rgba(74,138,255,0.15);
          color: #4a8aff;
          padding: 2px 12px;
          border-radius: 40px;
          border: 1px solid rgba(74,138,255,0.1);
        }
        .chart-wrapper {
          flex: 1;
          min-height: 0;
          position: relative;
        }
        .chart-wrapper canvas {
          max-height: 200px;
          width: 100% !important;
          height: auto !important;
        }

        /* ---------- PROGRESS SECTION ---------- */
        .progress-section {
          background: rgba(12, 28, 58, 0.5);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
          padding: clamp(0.8rem, 1.5vw, 1.6rem);
          margin-bottom: clamp(1.2rem, 2vw, 2rem);
          animation: fadeUp 0.6s ease;
        }
        .progress-section .section-title {
          font-size: clamp(0.95rem, 1.4vw, 1.15rem);
          font-weight: 600;
          margin-bottom: 14px;
          display: flex;
          align-items: center;
          gap: 10px;
          color: #f0f4ff;
        }
        .progress-item {
          margin-bottom: clamp(10px, 1.2vw, 16px);
        }
        .progress-item:last-child { margin-bottom: 0; }
        .progress-label {
          display: flex;
          justify-content: space-between;
          font-size: clamp(0.7rem, 1vw, 0.85rem);
          color: #9bb0d9;
          margin-bottom: 4px;
          font-weight: 500;
        }
        .progress-track {
          width: 100%;
          height: 8px;
          background: rgba(255,255,255,0.06);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: inset 0 1px 4px rgba(0,0,0,0.3);
        }
        .progress-fill {
          height: 100%;
          border-radius: 12px;
          transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* ---------- ERROR BANNER ---------- */
        .error-banner {
          background: rgba(239,68,68,0.12);
          border: 1px solid rgba(239,68,68,0.2);
          color: #f87171;
          padding: 10px 18px;
          border-radius: 14px;
          margin-bottom: 18px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
          animation: scaleIn 0.3s ease;
          backdrop-filter: blur(4px);
        }
        .error-banner .close {
          cursor: pointer;
          opacity: 0.6;
          font-size: 1.3rem;
          transition: opacity 0.2s;
          line-height: 1;
          padding: 0 4px;
        }
        .error-banner .close:hover { opacity: 1; }

        /* ---------- LOADER / ERROR PAGES ---------- */
        .loader-wrapper, .error-wrapper {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 80vh;
          color: #9bb0d9;
          font-family: 'Inter', -apple-system, sans-serif;
          text-align: center;
          padding: 20px;
        }
        .loader-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid rgba(255,255,255,0.08);
          border-top-color: #4a8aff;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin-bottom: 20px;
        }
        .loader-text {
          font-size: 1.1rem;
          font-weight: 500;
        }
        .retry-btn {
          margin-top: 20px;
          padding: 10px 28px;
          background: rgba(74,138,255,0.15);
          border: 1px solid #4a8aff;
          border-radius: 40px;
          color: #4a8aff;
          cursor: pointer;
          font-size: 1rem;
          font-weight: 500;
          transition: all 0.25s;
          touch-action: manipulation;
        }
        .retry-btn:hover {
          background: rgba(74,138,255,0.25);
          transform: scale(1.02);
        }
        .retry-btn:active { transform: scale(0.95); }

        /* ---------- TOAST ---------- */
        .toast-container {
          position: fixed;
          top: 24px;
          right: 24px;
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-width: 440px;
        }
        .toast-item {
          background: rgba(10, 20, 45, 0.8);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: 16px;
          padding: 14px 20px;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 14px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.4);
          animation: slideIn 0.35s ease;
        }
        .toast-item.success { border-left: 4px solid #4a8aff; }
        .toast-item.danger { border-left: 4px solid #ef4444; }
        .toast-item.warning { border-left: 4px solid #f59e0b; }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px) scale(0.96); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 4px;
          margin-left: auto;
          transition: color 0.2s;
        }
        .btn-close-custom:hover { color: var(--text-primary); }

        /* ---------- RESPONSIVE ---------- */
        @media (max-width: 1024px) {
          .chart-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 640px) {
          .dashboard-wrapper { padding: 10px; }
          .dashboard-header {
            flex-direction: column;
            align-items: stretch;
            gap: 10px;
            padding: 12px 16px;
          }
          .header-right {
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 6px;
          }
          .kpi-grid {
            grid-template-columns: 1fr 1fr;
            gap: 8px;
          }
          .kpi-card {
            padding: 10px 12px;
          }
          .kpi-value {
            font-size: clamp(1.2rem, 5vw, 1.6rem);
          }
          .kpi-value.small {
            font-size: clamp(0.8rem, 4vw, 1rem);
          }
          .chart-container {
            padding: 12px;
            min-height: 180px;
          }
          .chart-wrapper canvas {
            max-height: 160px;
          }
          .progress-label {
            font-size: 0.7rem;
          }
        }
        @media (max-width: 400px) {
          .kpi-grid { gap: 6px; }
          .kpi-card { padding: 8px; }
          .kpi-value { font-size: 1rem; }
          .kpi-label { font-size: 0.5rem; }
          .kpi-icon { font-size: 1rem; }
          .chart-container { min-height: 150px; padding: 8px; }
          .chart-wrapper canvas { max-height: 120px; }
        }
      `}</style>

      {/* HEADER */}
      <header className="dashboard-header">
        <h1>📈 Recrutement <small>Tableau de bord</small></h1>
        <div className="header-right">
          <div className="header-time">
            <span className="dot"></span>
            <span>{new Date().toLocaleString('fr-FR')}</span>
          </div>
          <span className={`badge-source ${source.includes('live') ? 'live' : ''}`}>
            {source === 'cache' ? '📦 Cache' :
             source === 'cache (périmé)' ? '⚠️ Cache' :
             source === 'cache (partagé)' ? '🔄 Partage' :
             source === 'live' ? '🔄 Live' :
             source === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
          </span>
          <button className="btn-refresh" onClick={refresh} disabled={isRefreshing || loading}>
            {isRefreshing ? <span className="spinner-small"></span> : '⟳'}
            {isRefreshing ? ' Chargement' : ' Rafraîchir'}
          </button>
          <button
            className="btn-clear-cache"
            onClick={handleClearCache}
            disabled={isRefreshing || loading}
            title="Vider le cache et forcer le rechargement"
          >
            <FontAwesomeIcon icon={faEraser} /> Vider le cache
          </button>
        </div>
      </header>

      {/* ERROR BANNER */}
      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <span className="close" onClick={clearError}>&times;</span>
        </div>
      )}

      {/* KPI CARDS */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-top">
            <span className="kpi-icon">📋</span>
            <span style={{ fontSize: '0.7rem', color: '#6a80aa' }}>total</span>
          </div>
          <div className="kpi-value">{totaux.offres}</div>
          <div className="kpi-label">Offres</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-top">
            <span className="kpi-icon">📩</span>
            <span style={{ fontSize: '0.7rem', color: '#6a80aa' }}>total</span>
          </div>
          <div className="kpi-value">{totaux.candidatures}</div>
          <div className="kpi-label">Candidatures</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-top">
            <span className="kpi-icon">👤</span>
            <span style={{ fontSize: '0.7rem', color: '#6a80aa' }}>total</span>
          </div>
          <div className="kpi-value">{totaux.candidats}</div>
          <div className="kpi-label">Candidats</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-top">
            <span className="kpi-icon">📊</span>
            <span style={{ fontSize: '0.7rem', color: '#6a80aa' }}>moy.</span>
          </div>
          <div className="kpi-value">{moyenneCandParOffre}</div>
          <div className="kpi-label">Moy. candidatures/offre</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-top">
            <span className="kpi-icon">🟢</span>
            <span style={{ fontSize: '0.7rem', color: '#6a80aa' }}>ouverts</span>
          </div>
          <div className="kpi-value">{postes.ouverts}</div>
          <div className="kpi-label">Postes ouverts</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-top">
            <span className="kpi-icon">🔵</span>
            <span style={{ fontSize: '0.7rem', color: '#6a80aa' }}>pourvus</span>
          </div>
          <div className="kpi-value">{postes.pourvus}</div>
          <div className="kpi-label">Postes pourvus</div>
        </div>
      </div>

      {/* GRAPHIQUES */}
      <div className="chart-grid">
        <div className="chart-container">
          <div className="chart-title">📊 Offres par statut <span className="badge">Barres</span></div>
          <div className="chart-wrapper">
            {finalData.statutsOffres && finalData.statutsOffres.length > 0 ? (
              <Bar data={barStatutsOffres} options={getChartOptions('bar')} />
            ) : (
              <div className="text-center text-secondary py-3">Aucune donnée de statut d'offre</div>
            )}
          </div>
        </div>
        <div className="chart-container">
          <div className="chart-title">🧩 Candidatures par statut <span className="badge">Pie</span></div>
          <div className="chart-wrapper">
            {finalData.statutsCandidatures && finalData.statutsCandidatures.length > 0 ? (
              <Pie data={pieCandidatures} options={getChartOptions('pie')} />
            ) : (
              <div className="text-center text-secondary py-3">Aucune donnée de statut de candidature</div>
            )}
          </div>
        </div>
        <div className="chart-container">
          <div className="chart-title">🏢 Offres par service <span className="badge">Barres</span></div>
          <div className="chart-wrapper">
            {finalData.services && finalData.services.length > 0 ? (
              <Bar data={barServices} options={getChartOptions('bar')} />
            ) : (
              <div className="text-center text-secondary py-3">Aucune donnée par service</div>
            )}
          </div>
        </div>
        <div className="chart-container">
          <div className="chart-title">👫 Répartition par sexe <span className="badge">Doughnut</span></div>
          <div className="chart-wrapper">
            {finalData.sexes && finalData.sexes.length > 0 ? (
              <Doughnut data={doughnutSexes} options={getChartOptions('doughnut')} />
            ) : (
              <div className="text-center text-secondary py-3">Aucune donnée de sexe</div>
            )}
          </div>
        </div>
        <div className="chart-container" style={{ gridColumn: '1 / -1' }}>
          <div className="chart-title">📈 Évolution des candidatures <span className="badge">12 mois</span></div>
          <div className="chart-wrapper">
            {finalData.evolution && finalData.evolution.length > 0 ? (
              <Line data={lineEvolution} options={getChartOptions('line')} />
            ) : (
              <div className="text-center text-secondary py-3">Aucune donnée d'évolution</div>
            )}
          </div>
        </div>
        <div className="chart-container" style={{ gridColumn: '1 / -1' }}>
          <div className="chart-title">🏆 Top 10 offres les plus sollicitées <span className="badge">Barres horizontales</span></div>
          <div className="chart-wrapper">
            {finalData.offresPopulaires && finalData.offresPopulaires.length > 0 ? (
              <Bar data={barPopulaires} options={getChartOptions('bar', true)} />
            ) : (
              <div className="text-center text-secondary py-3">Aucune offre populaire</div>
            )}
          </div>
        </div>
      </div>

      {/* BARRES DE PROGRESSION : NIVEAUX D'ÉTUDE */}
      <div className="progress-section">
        <div className="section-title">🎓 Répartition des candidats par niveau d'étude</div>
        {progressNiveaux.length > 0 ? (
          progressNiveaux.map((item, idx) => {
            const hue = (idx * 45) % 360;
            return (
              <div key={idx} className="progress-item">
                <div className="progress-label">
                  <span>{item.label}</span>
                  <span>{item.percentage.toFixed(1)}% ({item.count})</span>
                </div>
                <div className="progress-track">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${Math.min(item.percentage, 100)}%`,
                      background: `linear-gradient(90deg, hsl(${hue}, 70%, 50%), hsl(${hue + 30}, 70%, 60%))`
                    }}
                  />
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center text-secondary py-3">Aucune donnée de niveau d'étude</div>
        )}
      </div>

      {/* TOAST */}
      {toast && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-primary' : 'text-danger'}
            />
            <span>{toast.message}</span>
            <button className="btn-close-custom" onClick={() => setToast(null)}>✕</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardRecrutement;